/** @file Vuex Typex. */
/*
 * MIT License
 *
 * Copyright (c) 2017
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import { Store, } from 'vuex';
const useRootNamespace = { root: true };
class ModuleBuilderImpl {
    namespace;
    _initialState;
    _store;
    _getters = {};
    _mutations = {};
    _actions = {};
    _moduleBuilders = {};
    _vuexModule;
    constructor(namespace, _initialState) {
        this.namespace = namespace;
        this._initialState = _initialState;
    }
    state() {
        if (!this.namespace) {
            return () => this._store.state;
        }
        else if (!this.namespace.includes('/')) {
            return () => this._store.state[this.namespace];
        }
        else {
            const namespaces = this.namespace.split('/');
            return () => {
                let accessor = this._store.state;
                for (const name of namespaces) {
                    accessor = accessor[name];
                }
                return accessor;
            };
        }
    }
    setInitialState(initialState) {
        this._initialState = initialState;
    }
    module(namespace, initialState) {
        const existingModule = this._moduleBuilders[namespace];
        const qualifiedNamespace = qualifyNamespace(this.namespace, namespace);
        if (!initialState && existingModule) {
            return existingModule;
        }
        // both arguments: create a module
        if (existingModule && initialState) {
            existingModule.setInitialState(initialState);
            return existingModule;
        }
        const nestedBuilder = new ModuleBuilderImpl(qualifiedNamespace, initialState || null);
        this._moduleBuilders[namespace] = nestedBuilder;
        return nestedBuilder;
    }
    commit(handler, name) {
        const { key, namespacedKey } = qualifyKey(handler, this.namespace, name);
        if (this._mutations[key]) {
            throw new Error(`There is already a mutation named ${key}.`);
        }
        this._mutations[key] = handler;
        return ((payload) => this._store.commit(namespacedKey, payload, useRootNamespace));
    }
    dispatch(handler, name) {
        const { key, namespacedKey } = qualifyKey(handler, this.namespace, name);
        if (this._actions[key]) {
            throw new Error(`There is already an action named ${key}.`);
        }
        this._actions[key] = handler;
        return (payload) => this._store.dispatch(namespacedKey, payload, useRootNamespace);
    }
    read(handler, name) {
        const { key, namespacedKey } = qualifyKey(handler, this.namespace, name);
        if (this._getters[key]) {
            throw new Error(`There is already a getter named ${key}.`);
        }
        this._getters[key] = handler;
        return () => {
            if (this._store.rootGetters) {
                return this._store.rootGetters[namespacedKey];
            }
            return this._store.getters[namespacedKey];
        };
    }
    vuexModule() {
        if (!this._vuexModule) {
            // build nested modules recursively, if any
            const modules = {};
            for (const namespace of Object.keys(this._moduleBuilders)) {
                modules[namespace] = this._moduleBuilders[namespace].vuexModule();
            }
            this._vuexModule = {
                namespaced: true,
                state: this._initialState || {},
                getters: this._getters,
                mutations: this._mutations,
                actions: this._actions,
                modules,
            };
        }
        return this._vuexModule;
    }
    _provideStore(store) {
        this._store = store;
        forEachValue(this._moduleBuilders, m => m._provideStore(store));
    }
}
function qualifyKey(handler, namespace, name) {
    const key = name || handler.name;
    if (!key) {
        throw new Error(`Vuex handler functions must not be anonymous. Possible causes: fat-arrow functions, uglify.  To fix, pass a unique name as a second parameter after your callback.`);
    }
    return { key, namespacedKey: qualifyNamespace(namespace, key) };
}
function qualifyNamespace(namespace, key) {
    return namespace ? `${namespace}/${key}` : key;
}
class StoreBuilderImpl extends ModuleBuilderImpl {
    constructor() {
        super('', {});
    }
    module(namespace, initialState) {
        return super.module(namespace, initialState);
    }
    vuexStore(overrideOptions = {}) {
        if (!this._store) {
            const options = {
                ...this.vuexModule(),
                ...overrideOptions,
            };
            const store = new Store(options);
            forEachValue(this._moduleBuilders, m => m._provideStore(store));
            this._store = store;
        }
        return this._store;
    }
    registerModule(namespace) {
        if (this._store && this._vuexModule) {
            const mBuilder = this._moduleBuilders[namespace];
            if (!mBuilder)
                throw `fail to register module: ${namespace}`;
            mBuilder._provideStore(this._store);
            const vModule = mBuilder.vuexModule();
            this._store.registerModule(namespace, vModule);
            this._vuexModule.modules[namespace] = vModule;
        }
        else {
            throw "vuexStore hasn't been called yet, use module() instead.";
        }
    }
    reset() {
        this._store = undefined;
        this._moduleBuilders = {};
    }
}
const forEachValue = (dict, loop) => {
    Object.keys(dict).forEach(key => loop(dict[key]));
};
const storeBuilderSingleton = new StoreBuilderImpl();
const namedStoreBuilderMap = Object.create(null);
export function getStoreBuilder(name) {
    // the default store builder
    if (!name) {
        return storeBuilderSingleton;
    }
    // a named store builder
    const builder = namedStoreBuilderMap[name] || (namedStoreBuilderMap[name] = new StoreBuilderImpl());
    return builder;
}
